/**
 * Admin Finanzas – financial overview from billingCycles. Status = billingCycle.studentId ? "Pagado" : "Pendiente de pago".
 * Data from useBillingCycles() (client-side join and status computation). UI unchanged.
 */

import { useState, useMemo } from "react";
import { useBillingCycles } from "../../modules/finance/hooks/useBillingCycles";
import type { PaymentGroupRow } from "../../types";

// --- Helpers (options) ---

function getMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [{ value: "", label: "Todos los meses" }];
  const now = new Date();
  const monthNames = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = d.getMonth();
    const value = `${y}-${String(m + 1).padStart(2, "0")}`;
    options.push({ value, label: `${monthNames[m]} ${y}` });
  }
  return options;
}

const STATUS_PILL_OPTIONS = ["Todos", "Pendiente de pago", "Pagado"] as const;

// --- Icons (inline SVG to avoid lucide-react dependency) ---

const IconSearch = () => (
  <svg className="w-4 h-4 text-[#9CA3AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);
const IconDownload = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);
const IconCalendar = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);
const IconChevronDown = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);
const IconMoreHorizontal = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01M5 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
  </svg>
);
const IconTrendingUp = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);
const IconClock = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const IconAlertCircle = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// --- Summary Card (compact: table is the focus) ---

function SummaryCard({
  label,
  value,
  subtitle,
  accentColor,
}: {
  label: string;
  value: string;
  subtitle: string;
  accentColor: "green" | "yellow" | "red";
}) {
  const colors = {
    green: { bg: "bg-[#ECFDF5]", text: "text-[#059669]", Icon: IconTrendingUp },
    yellow: { bg: "bg-[#FFFBEB]", text: "text-[#D97706]", Icon: IconClock },
    red: { bg: "bg-[#FEF2F2]", text: "text-[#DC2626]", Icon: IconAlertCircle },
  };
  const config = colors[accentColor];
  const Icon = config.Icon;

  return (
    <div className="bg-white px-4 py-3 rounded-xl border border-[#E5E7EB] shadow-sm flex items-center gap-3">
      <div className={`p-1.5 rounded-lg shrink-0 ${config.bg} ${config.text} [&_svg]:w-4 [&_svg]:h-4`}>
        <Icon />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-medium text-[#6B7280] uppercase tracking-wider">{label}</p>
        <p className="text-base font-bold text-[#111827] tracking-tight">{value}</p>
        <p className="text-[10px] text-[#9CA3AF] mt-0.5">{subtitle}</p>
      </div>
    </div>
  );
}

// --- Status Badge (reference: green/amber/red/gray) ---

function StatusBadge({ status }: { status: string }) {
  const styleByLabel: Record<string, string> = {
    Pagado: "bg-[#ECFDF5] text-[#059669] border border-[#D1FAE5]",
    "Pendiente de pago": "bg-[#FFFBEB] text-[#D97706] border border-[#FEF3C7]",
    Cancelado: "bg-[#F3F4F6] text-[#6B7280] border border-[#E5E7EB]",
  };

  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap ${
        styleByLabel[status] ?? styleByLabel["Pendiente de pago"]
      }`}
    >
      {status}
    </span>
  );
}

// --- Row details modal (opened by ... dots) ---

function RowDetailsModal({
  row,
  onClose,
}: {
  row: PaymentGroupRow;
  onClose: () => void;
}) {
  const paymentMethodLabel =
    row.paymentMethodDisplay ??
    (row.paymentMethod === "MANUAL" ? "Manual" : row.paymentMethod === "AZUL" ? "Tarjeta" : "—");

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" aria-hidden onClick={onClose} />
      <div
        className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white shadow-xl border border-[#E5E7EB] p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="row-details-title"
      >
        <div className="flex items-center justify-between gap-2 mb-3">
          <h2 id="row-details-title" className="text-sm font-bold text-[#111827]">
            Detalles de la transacción
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-[#6B7280] hover:text-[#111827] p-1 rounded"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
        <dl className="space-y-2 text-[11px]">
          <div>
            <dt className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Alumno</dt>
            <dd className="font-medium text-[#111827]">{row.studentName || "—"}</dd>
            <dt className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider mt-2">Id</dt>
            <dd className="text-[#6B7280] font-mono text-sm">{row.studentId || "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Clases</dt>
            <dd className="text-[#374151]">{row.classNames?.length ? row.classNames.join(", ") : "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Mes</dt>
            <dd className="text-[#374151]">{row.periodLabel || "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Monto total</dt>
            <dd className="font-semibold text-[#111827]">
              RD$ {row.totalAmount.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Estado</dt>
            <dd><StatusBadge status={row.status} /></dd>
          </div>
          <div>
            <dt className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Método de pago</dt>
            <dd className="text-[#374151]">{paymentMethodLabel}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">Comprobante</dt>
            <dd className="text-[#374151]">{row.comprobanteDisplay ?? "—"}</dd>
          </div>
        </dl>
        <div className="mt-4 pt-3 border-t border-[#F3F4F6]">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2 rounded-lg bg-[#F3F4F6] text-[11px] font-semibold text-[#374151] hover:bg-[#E5E7EB]"
          >
            Cerrar
          </button>
        </div>
      </div>
    </>
  );
}

// --- Export report (CSV of current filtered groups) ---

function exportGroupsToCsv(groups: PaymentGroupRow[]) {
  const headers = ["Alumno", "Id", "Clases", "Mes", "Monto Total", "Estado", "Método de Pago", "Comprobante"];
  const rows = groups.map((row) => {
    const method = row.paymentMethod === "MANUAL" ? "Manual" : row.paymentMethod === "AZUL" ? "Tarjeta" : "";
    return [row.studentName || "—", row.studentId || "—", row.classNames.join(", "), row.periodLabel, row.totalAmount, row.status, method, row.comprobanteDisplay];
  });
  const csvContent = [headers.join(","), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `finanzas_pagos_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// --- Main Screen ---

const DEFAULT_TABLE_LIMIT = 5;
const EXPANDED_TABLE_LIMIT = 20;

export function FinanzasScreen() {
  const { groups, totals, loading, error } = useBillingCycles();
  const [filterStatusPill, setFilterStatusPill] = useState<string>("Todos");
  const [monthFilter, setMonthFilter] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const [displayLimit, setDisplayLimit] = useState<number>(DEFAULT_TABLE_LIMIT);
  const [detailsRow, setDetailsRow] = useState<PaymentGroupRow | null>(null);

  const monthOptions = getMonthOptions();
  const currentMonthLabel = monthFilter ? monthOptions.find((o) => o.value === monthFilter)?.label ?? "Todos los meses" : "Todos los meses";

  const filteredGroups = useMemo(() => {
    let list = groups;
    if (filterStatusPill !== "Todos") {
      list = list.filter((row) => row.status === filterStatusPill);
    }
    if (monthFilter) {
      list = list.filter((row) => row.periodYYYYMM === monthFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(
        (row) =>
          row.studentName.toLowerCase().includes(q) ||
          row.studentId.toLowerCase().includes(q) ||
          row.classNames.some((cn) => cn.toLowerCase().includes(q))
      );
    }
    return list;
  }, [groups, filterStatusPill, monthFilter, searchQuery]);

  const displayedGroups = useMemo(
    () => filteredGroups.slice(0, displayLimit),
    [filteredGroups, displayLimit]
  );
  const hasMore = filteredGroups.length > DEFAULT_TABLE_LIMIT;
  const showingExpanded = displayLimit >= EXPANDED_TABLE_LIMIT;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-black/[0.02]">
      <div className="p-5 space-y-6">
        {/* HEADER */}
        <div className="space-y-1">
          <h1 className="text-xl font-bold tracking-tight text-[#161b3d]">Finanzas</h1>
          <p className="text-xs text-[#86868b]">
            Resumen financiero del sistema (ciclos de facturación).
          </p>
        </div>

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-[12px] text-red-700">
            {error}
          </div>
        )}

        {/* SECTION 1 — SUMMARY CARDS (compact; table is primary) */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SummaryCard
            label="Ingresos confirmados"
            value={`RD$ ${totals.totalPaidAmount.toLocaleString("es-DO", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
            subtitle={`${totals.countPaid} pagado(s)`}
            accentColor="green"
          />
          <SummaryCard
            label="Pendientes"
            value={`RD$ ${totals.totalPendingAmount.toLocaleString("es-DO", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
            subtitle={`${totals.countPending} pendiente(s) de pago`}
            accentColor="yellow"
          />
        </section>

        {/* SECTION 2 — FILTER BAR */}
        <section className="border border-gray-100 rounded-2xl bg-[#f9fafb]/60 px-4 py-4">
          <div className="space-y-4">
            {/* Search + month + export */}
            <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-4">
              <div className="relative w-full lg:max-w-xs">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <IconSearch />
                </span>
                <input
                  type="text"
                  placeholder="Buscar alumno o clase..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#111827]/5 transition-all"
                />
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto lg:justify-end">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setMonthDropdownOpen(!monthDropdownOpen)}
                    className="px-4 py-2 bg-white border border-[#E5E7EB] rounded-lg text-sm font-medium text-[#374151] hover:bg-[#F9FAFB] transition-colors flex items-center gap-2 w-full sm:w-auto"
                  >
                    <IconCalendar />
                    <span>{currentMonthLabel}</span>
                    <IconChevronDown />
                  </button>
                  {monthDropdownOpen && (
                    <>
                      <div className="fixed inset-0 z-10" aria-hidden onClick={() => setMonthDropdownOpen(false)} />
                      <div className="absolute left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-[#E5E7EB] z-20 py-1 max-h-60 overflow-auto">
                        {monthOptions.map((o) => (
                          <button
                            key={o.value || "all"}
                            type="button"
                            onClick={() => {
                              setMonthFilter(o.value);
                              setMonthDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-[#F9FAFB] ${
                              monthFilter === o.value ? "bg-[#F3F4F6] font-medium text-[#111827]" : "text-[#374151]"
                            }`}
                          >
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => exportGroupsToCsv(filteredGroups)}
                  className="px-4 py-2 bg-white border border-[#E5E7EB] rounded-lg text-sm font-medium text-[#374151] hover:bg-[#F9FAFB] transition-colors flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-start"
                >
                  <IconDownload />
                  <span>Exportar reporte</span>
                </button>
              </div>
            </div>

            {/* Status pills */}
            <div className="overflow-x-auto">
              <div className="inline-flex items-center gap-1 bg-[#F3F4F6] p-1 rounded-lg border border-[#E5E7EB]">
                {STATUS_PILL_OPTIONS.map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => setFilterStatusPill(status)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                      filterStatusPill === status ? "bg-white shadow-sm text-[#111827]" : "text-[#6B7280] hover:text-[#111827]"
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3 — BILLING TABLE (Alumno, Mes, Monto total, Estado + ... for details) */}
        <section className="rounded-2xl border border-[#E5E7EB] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#F3F4F6] bg-[#F9FAFB]/50">
                  <th className="px-3 sm:px-4 py-2.5 text-[10px] font-medium text-[#6B7280] uppercase tracking-wider">
                    Alumno
                  </th>
                  <th className="px-3 sm:px-4 py-2.5 text-[10px] font-medium text-[#6B7280] uppercase tracking-wider">
                    Id
                  </th>
                  <th className="px-3 sm:px-4 py-2.5 text-[10px] font-medium text-[#6B7280] uppercase tracking-wider text-center">
                    Mes
                  </th>
                  <th className="px-3 sm:px-4 py-2.5 text-[10px] font-medium text-[#6B7280] uppercase tracking-wider text-right">
                    Monto Total
                  </th>
                  <th className="px-3 sm:px-4 py-2.5 text-[10px] font-medium text-[#6B7280] uppercase tracking-wider text-center">
                    Estado
                  </th>
                  <th className="px-3 sm:px-4 py-2.5 w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {filteredGroups.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 sm:px-4 py-8 text-center text-xs text-[#6B7280]">
                      No hay pagos que coincidan con los filtros.
                    </td>
                  </tr>
                ) : (
                  displayedGroups.map((row) => (
                    <tr
                      key={row.id}
                      className="hover:bg-gray-50 transition-colors group cursor-default"
                    >
                      <td className="px-3 sm:px-4 py-2.5">
                        <span className="text-xs font-semibold text-[#111827]">{row.studentName || "—"}</span>
                      </td>
                      <td className="px-3 sm:px-4 py-2.5">
                        <span className="text-[11px] font-mono text-[#6B7280]">{row.studentId || "—"}</span>
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 text-center text-[11px] text-[#374151]">
                        {row.periodLabel}
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 text-right">
                        <span className="text-xs font-semibold text-[#111827]">
                          RD$ {row.totalAmount.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 text-center">
                        <StatusBadge status={row.status} />
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 text-right">
                        <button
                          type="button"
                          onClick={() => setDetailsRow(row)}
                          className="p-1.5 rounded-md hover:bg-[#F3F4F6] text-[#6B7280] hover:text-[#111827] transition-colors"
                          aria-label="Ver detalles"
                        >
                          <IconMoreHorizontal />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {detailsRow && (
            <RowDetailsModal row={detailsRow} onClose={() => setDetailsRow(null)} />
          )}
          {filteredGroups.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-4 py-2.5 border-t border-[#F3F4F6] bg-[#F9FAFB]/30">
              <span className="text-[11px] text-[#6B7280]">
                Mostrando {displayedGroups.length} de {filteredGroups.length} transacciones
                {(filterStatusPill !== "Todos" || monthFilter || searchQuery.trim()) && " (filtros aplicados)"}
              </span>
              {hasMore && (
                <button
                  type="button"
                  onClick={() => setDisplayLimit(showingExpanded ? DEFAULT_TABLE_LIMIT : EXPANDED_TABLE_LIMIT)}
                  className="text-[11px] font-semibold text-[#5856D6] hover:text-[#4a48b8] hover:underline"
                >
                  {showingExpanded ? "Ver solo 5" : "Ver últimos 20"}
                </button>
              )}
            </div>
          )}
        </section>
        {loading && groups.length === 0 && (
          <div className="flex items-center gap-2 text-[12px] text-[#86868b]">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#5856D6]/30 border-t-[#5856D6]" />
            <span>Cargando finanzas…</span>
          </div>
        )}
      </div>
    </div>
  );
}
